import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import ThreadsIcon from '../src/components/ui/ThreadsIcon';
import { SOCIAL_PROFILE_URLS } from '../lib/socialLinks';
import { STATE_NAV_GROUPS } from '../lib/stateRoutes';

/**
 * Social links, left-to-right order. Icon is a lucide component (or the custom
 * ThreadsIcon drawn to lucide's spec) — all rendered uniformly with a className and
 * inheriting colour via currentColor. ComponentType<any> so the lucide and custom icon
 * components share one slot type.
 *
 * hrefs come from lib/socialLinks.ts — the same list index.html's Organization node
 * declares as `sameAs`, so the visible links and the ownership claim we make to search
 * engines can never disagree.
 *
 * UI-47 (operator-ruled): the ICON row is restored. UI-43 had briefly rendered these as
 * text + an outbound arrow inside a fourth "Follow us" column; that column is removed
 * and the brand marks are back — which also answers the review's note that four
 * identical arrow+text rows scanned worse than four distinct glyphs.
 *
 * UI-48 (operator-ruled 2026-08-10): the row MOVED from the bottom strip to directly
 * below the wordmark, at EVERY viewport — web, tablet and mobile. One DOM instance is
 * moved, deliberately: rendering it twice and hiding one per breakpoint would put four
 * duplicate outbound links into all 4,221 prerendered pages, since CSS-hidden markup
 * survives prerendering. Position is pinned by Footer.structure.test.tsx.
 */
const [FACEBOOK_URL, INSTAGRAM_URL, THREADS_URL, YOUTUBE_URL] = SOCIAL_PROFILE_URLS;
const SOCIAL_LINKS: ReadonlyArray<{ label: string; href: string; Icon: React.ComponentType<any> }> = [
  { label: 'Facebook', href: FACEBOOK_URL!, Icon: Facebook },
  { label: 'Instagram', href: INSTAGRAM_URL!, Icon: Instagram },
  { label: 'Threads', href: THREADS_URL!, Icon: ThreadsIcon },
  { label: 'YouTube', href: YOUTUBE_URL!, Icon: Youtube },
];

/** Internal columns. Labels authored in-session per the 2026-08-08 operator ruling. */
// ⭐ ORDER IS OPERATOR-RULED (2026-08-17): Blog · About Us · Contact. It is not
// alphabetical and not arbitrary — Blog is the only one of the three a visitor has a
// reason to open repeatedly, so it leads. ⚠️ THE ORDER IS THE RULING, so it is pinned
// by an assertion in Footer.structure.test.tsx: the pre-existing guard there checks
// label→href PAIRS, which a reorder cannot break, so without that assertion this
// change would ship entirely untested.
const ABOUT_LINKS: ReadonlyArray<{ label: string; to: string }> = [
  { label: 'Blog', to: '/blog' },
  { label: 'About Us', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const LEGAL_LINKS: ReadonlyArray<{ label: string; to: string }> = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
];

const Footer: React.FC = () => {
  // Brand keyboard focus ring for the footer's links/icons (matches the Header). Inset
  // so it reads cleanly on the dark surface-ink footer without a ring-offset colour.
  const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-on-dark focus-visible:ring-inset';
  const linkClass =
    `inline-flex items-center min-h-[44px] rounded-sm text-sm text-text-subtle font-medium hover:text-white transition-colors ${focusRing}`;

  /**
   * Column headings are REAL <h2>s (UI-43). They began as styled divs to protect the
   * document outline of 4,221 prerendered pages; Content & SEO — who own that surface —
   * measured the premise and refuted it (a live gym page already carries 1 h1 and SIX
   * h2s), so the accessibility case decides it: screen-reader users navigate BY HEADING,
   * which aria-labelledby grouping cannot provide.
   */
  const headingClass = 'text-white font-semibold text-base mb-4';

  return (
    <footer className="bg-surface-ink py-12" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* ⚠️ FOUR link columns since 2026-08-16 (the Browse column became one per state),
            and THAT IS WHY THE MOBILE RULE CHANGED — operator-ruled, with the measurement
            in front of them.

            UI-47 had ruled THREE columns side by side at EVERY viewport, on a measurement:
            98px per column at 375px, which fits every label except "Terms of Service"
            (114px, accepted as a two-line wrap). ⭐ THAT MEASUREMENT WAS FOR THREE. A fourth
            column takes each to ~70px at the same width, where the heading "New South Wales"
            wraps to three lines and "NSW Suburbs" wraps too — a footer that looks correct on
            the desktop an operator reviews from and cramped on the phone most visitors use.

            So: TWO columns below sm (a 2×2 block, each ~98px again — the width UI-47
            measured and accepted), FOUR from sm up, and the brand keeps two of six tracks at
            lg. UI-47's "side by side at every viewport" is superseded for four columns and
            intact in spirit: nothing stacks into a single lonely row, which was the outcome
            that ruling existed to prevent.

            gap-x stays tight below sm so the 98px columns are not squeezed further. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-10 gap-x-4 sm:gap-x-8 lg:grid-cols-6">
          {/* Brand block — the plain text wordmark, restored to its pre-UI-43 form
              (operator-ruled UI-47). The belt mark UI-43 introduced is gone, which also
              removes the 162KB asset — and React 19's high-priority preload of it — from
              all 4,221 prerendered pages entirely, rather than merely deferring it.
              Spans the full width below lg so it sits above the columns. */}
          {/* Spans the FULL width below lg so it sits above the columns — restated for the
              new track counts: 2 of 2 on phones, 4 of 4 at sm, 2 of 6 at lg. These must
              track the grid above; a stale span here silently drops the brand into a
              column slot instead of its own row. */}
          <div className="col-span-2 sm:col-span-4 lg:col-span-2">
            <div className="font-semibold text-xl text-white">
              Martial Arts Network
            </div>
            {/* Social row — icon-only, sitting directly BELOW the wordmark at EVERY
                viewport (operator-ruled 2026-08-10; previously it lived in the bottom
                strip above the copyright, which now carries the copyright alone).
                mt-4 = the same 16px rhythm the column headings use (mb-4).
                -ml-3 pulls the row left so the first icon — centred in its 44px hit
                box — aligns optically with the "M" of the wordmark above it. That is
                the identical optical correction it previously made against the
                copyright line; only its reference text changed.
                Icons are decorative; the accessible name is the aria-label. */}
            <div className="flex items-center gap-1 -ml-3 mt-4">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`inline-flex items-center justify-center h-11 w-11 rounded-full text-text-subtle hover:text-white transition-colors ${focusRing}`}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* ⭐ ONE COLUMN PER STATE — "New South Wales" and "Victoria" (operator-ruled
              2026-08-16), replacing the single "Browse" column. Each carries that state's
              directory and its suburb A–Z, in that order.

              UI-09 — these columns carry the ONLY directory links in the chrome that
              survive prerendering. The Header's equivalents live inside a dropdown and the
              mobile drawer, neither of which is rendered into the static HTML, so before
              this row a gym page's prerendered markup contained ZERO chrome directory links
              (measured: 0). ⭐ The count now DOUBLES — each state contributes two anchors
              rather than one — so every prerendered page gains its state directory link,
              which the chrome previously never carried at all.

              Derived from STATE_NAV_GROUPS, shared with the Header, so the two surfaces
              cannot drift and a third state adds its own column with no edit here.

              ⚠️ THE <nav aria-label> LANDMARKS ARE PART OF THE SHIPPED UI-09 FIX — KEEP THEM.
              There is now one per state, each labelled with the state, because two landmarks
              sharing the label "Browse gyms by state" would be indistinguishable to a screen
              reader navigating by landmark — which is the whole reason the label exists. */}
          {STATE_NAV_GROUPS.map((group) => (
            <nav key={group.slug} aria-label={`Browse gyms in ${group.full}`}>
              <h2 className={headingClass}>{group.full}</h2>
              {/* role="list" is load-bearing: Tailwind's preflight sets list-style:none and
                  WebKit drops the AXList role from such a list unless it is explicit. */}
              <ul role="list" className="flex flex-col">
                {group.links.map((link) => (
                  <li key={link.id}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <div>
            <h2 className={headingClass}>About</h2>
            <ul role="list" className="flex flex-col">
              {ABOUT_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className={headingClass}>Legal</h2>
            <ul role="list" className="flex flex-col">
              {LEGAL_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip — the divider and the copyright are kept exactly as they were
            (operator-ruled 2026-08-10). The icon row that used to sit above the
            copyright now lives under the wordmark, so this strip holds the copyright
            alone; the former flex column + gap-4 went with it, as a single child makes
            both inert. */}
        <div className="border-t border-border-default mt-10 pt-6">
          {/* The year is baked into all 4,202 prerendered pages as a discrete text
              node, but re-evaluates at hydration on the visitor's clock. From the
              first moment a visitor's local year ticks over until the next deploy
              rebakes it, every page would hydrate the new year against the baked one
              — a site-wide React #418 text mismatch. suppressHydrationWarning scopes
              the escape hatch to just this value, so genuine mismatches elsewhere in
              the footer still surface. React keeps the baked year for that page view;
              the next deploy rebakes it, so the staleness self-heals.
              Added 2026-08-15 (JAVASCRIPT-REACT-C triage). */}
          <p className="text-text-subtle text-sm">© <span suppressHydrationWarning>{new Date().getFullYear()}</span> Martial Arts Network.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
